import { Inputs } from '@/_models/virtualStaging/Tube/component/types';
import { Tween } from 'three/examples/jsm/libs/tween.module';
import {
  makeTubeComponent,
  tubeComponentType,
} from '@/_models/virtualStaging/Tube/component/Tube.component';
import {
  PIPES_CONSTANTS,
  PIPES_CONSTANTS_AMERICAN_WATER,
} from '@/_models/virtualStaging/Tube/constants';
import { getRandomRange } from '@/utils/getRandomRange';

const initTubeObject = async (options: Partial<Inputs>, showcaseTemp) => {
  await showcaseTemp.showcase.Scene.register(tubeComponentType, makeTubeComponent);
  const modelNode: any = await showcaseTemp.mpSDK.Scene.createNode();
  const component = await modelNode.addComponent(tubeComponentType, options);
  await modelNode.start();
  return component;
};

const getPolygonByTitle = (title, polygons, modelNodes) => {
  const uuid = polygons.find((polygon) => polygon.title === title)?.uuid;
  if (uuid) {
    return modelNodes.find((node) => node.length === 2 && node[0].obj3D.uuid === uuid);
  }
};

export const initPolygonUpdateFromVideo = (
  asset,
  node,
  pipesData,
  THREE,
  renderer,
  targetPolygonIndex = 0,
) => {
  const targetPolygon = pipesData.polygonArr[targetPolygonIndex];
  const linkedPipes = pipesData.components.filter(
    (component) => component.inputs.linkedPolygon?.id === targetPolygon.id,
  );

  const lightGreen = new THREE.Color('#5cab89');
  const green = new THREE.Color('green');
  const red = new THREE.Color('#ff0000');
  const blue = new THREE.Color('#0000ff');

  const pipeColor = {
    current: new THREE.Color('white'),
  };

  const setColor = (polygonColor, color) => {
    targetPolygon.polygon.material.color.set(polygonColor);
    linkedPipes.forEach((component) => {
      component.inputs.color = `#${color.getHexString()}`;
    });
    pipeColor.current.set(color);
  };

  const animate = () => {
    requestAnimationFrame(animate);
    if (!renderer?.xr?.isPresenting) {
      if (
        (node[1].video.currentTime < 2 || node[1].video.currentTime > 10) &&
        !pipeColor.current.equals(blue)
      ) {
        setColor(lightGreen, blue);
      }
      if (
        node[1].video.currentTime >= 2 &&
        node[1].video.currentTime <= 9 &&
        !pipeColor.current.equals(green)
      ) {
        setColor(lightGreen, green);
      }
      if (
        node[1].video.currentTime > 4 &&
        node[1].video.currentTime <= 8 &&
        !pipeColor.current.equals(red)
      ) {
        setColor(red, red);
      }
      if (
        node[1].video.currentTime > 8 &&
        node[1].video.currentTime <= 10 &&
        !pipeColor.current.equals(green)
      ) {
        setColor(lightGreen, blue);
      }
    }
  };

  animate();
};

export const initPipes = async (showcaseTemp, modelNodes, polygons, renderer) => {
  const polygonArr = [];
  PIPES_CONSTANTS.POLYGONS.forEach((title) => {
    const polygon = getPolygonByTitle(title, polygons, modelNodes);
    if (polygon?.length === 2) {
      polygon[1].outputs.collider = null;
      polygonArr.push(polygon[1]);
    }
  });

  const bottomPipe1 = await initTubeObject(
    {
      position: { x: 8.44, y: 0.58, z: 24.7 },
      rotation: { x: 0, y: Math.PI, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 10, y: 5 },
      textureOffset: { x: 0, y: 0.5 },
      length: 5.5,
      width: 0.21,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[0],
    },
    showcaseTemp,
  );

  const bottomPipe2 = await initTubeObject(
    {
      position: { x: 8.44, y: 0.58, z: 18.8 },
      rotation: { x: 0, y: Math.PI, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 30, y: 5 },
      textureOffset: { x: 0, y: 0.5 },
      length: 15,
      width: 0.21,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[1],
    },
    showcaseTemp,
  );

  const verticalPipeUp1 = await initTubeObject(
    {
      position: { x: 8.44, y: 0.9, z: 3.34 },
      rotation: { x: Math.PI / 2, y: Math.PI, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 2, y: 4 },
      textureOffset: { x: 0, y: 0.5 },
      length: 0.7,
      width: 0.21,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[1],
    },
    showcaseTemp,
  );

  const verticalPipeUp2 = await initTubeObject(
    {
      position: { x: 8.44, y: 2.2, z: 3.34 },
      rotation: { x: Math.PI / 2, y: Math.PI, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 4, y: 4 },
      textureOffset: { x: 0, y: 0.5 },
      length: 1,
      width: 0.21,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[2],
    },
    showcaseTemp,
  );

  const verticalPipeDown1 = await initTubeObject(
    {
      position: { x: 4.97, y: 2.88, z: 2.47 },
      rotation: { x: Math.PI * 1.5, y: Math.PI, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 6, y: 4 },
      textureOffset: { x: 0, y: 0.5 },
      length: 2.25,
      width: 0.14,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[3],
    },
    showcaseTemp,
  );

  const verticalPipeDown2 = await initTubeObject(
    {
      position: { x: 5.03, y: 2.88, z: 4.89 },
      rotation: { x: Math.PI * 1.5, y: Math.PI, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 6, y: 4 },
      textureOffset: { x: 0, y: 0.5 },
      length: 2.25,
      width: 0.14,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[4],
    },
    showcaseTemp,
  );

  const verticalPipeDown3 = await initTubeObject(
    {
      position: { x: 8.44, y: 2.64, z: 25.1 },
      rotation: { x: Math.PI * 1.5, y: Math.PI, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 4, y: 4 },
      textureOffset: { x: 0, y: 0.5 },
      length: 1.8,
      width: 0.21,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[0],
    },
    showcaseTemp,
  );

  const upperPipeLong = await initTubeObject(
    {
      position: { x: 7.9, y: 3.49, z: 3.31 },
      rotation: { x: 0, y: Math.PI * 1.5, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 5, y: 4 },
      textureOffset: { x: 0, y: 0.5 },
      length: 2.3,
      width: 0.21,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[2],
    },
    showcaseTemp,
  );

  const upperPipeLong2 = await initTubeObject(
    {
      position: { x: -4.18, y: 3.06, z: 26.54 },
      rotation: { x: 0, y: Math.PI / 2, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 30, y: 4 },
      textureOffset: { x: 0, y: 0.5 },
      length: 12.3,
      width: 0.21,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[0],
    },
    showcaseTemp,
  );

  const upperPipeShort = await initTubeObject(
    {
      position: { x: 4.99, y: 3.49, z: 3.95 },
      rotation: { x: 0, y: 0, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 4, y: 5 },
      textureOffset: { x: 0, y: 0.5 },
      length: 0.7,
      width: 0.14,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[4],
    },
    showcaseTemp,
  );

  const outsidePipe1 = await initTubeObject(
    {
      position: { x: 17.28, y: 0.69, z: 9.25 },
      rotation: { x: 0, y: 3.13, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 5, y: 4 },
      textureOffset: { x: 0, y: 0.5 },
      length: 6.2,
      width: 0.26,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[5],
    },
    showcaseTemp,
  );

  const components = [
    bottomPipe1,
    bottomPipe2,
    verticalPipeUp1,
    verticalPipeUp2,
    verticalPipeDown1,
    verticalPipeDown2,
    verticalPipeDown3,
    upperPipeLong,
    upperPipeLong2,
    upperPipeShort,
    outsidePipe1,
  ];

  components.forEach((component) => {
    component.renderer = renderer;
  });

  return { components, polygonArr };
};

const animateDustPipe = (component) => {
  const tweenUp = new Tween(component.root.scale)
    .to({ y: 2.3 }, getRandomRange(2000, 4000))
    .easing(Tween.Easing.Quadratic.InOut); // Change this to suit your needs
  const tweenDown = new Tween(component.root.scale)
    .to({ y: 1 }, getRandomRange(2000, 4000))
    .easing(Tween.Easing.Quadratic.InOut); // Change this to suit your needs

  // Modify onComplete to assign new random duration when tween finishes
  tweenUp.onComplete(() => {
    tweenDown.to({ y: getRandomRange(1, 2.3) }, getRandomRange(2000, 4000));
  });

  tweenDown.onComplete(() => {
    tweenUp.to({ y: getRandomRange(1, 2.3) }, getRandomRange(2000, 4000));
  });
  tweenUp.chain(tweenDown);
  tweenDown.chain(tweenUp);
  tweenUp.start();
};

export const initPipesAmericanWater = async (showcaseTemp, modelNodes, polygons, renderer) => {
  const polygonArr = [];
  PIPES_CONSTANTS_AMERICAN_WATER.POLYGONS.forEach((title) => {
    const polygon = getPolygonByTitle(title, polygons, modelNodes);
    if (polygon?.length === 2) {
      polygon[1].outputs.collider = null;
      polygonArr.push(polygon[1]);
    }
  });

  const outsidePipeVertical = await initTubeObject(
    {
      position: {
        x: 1.63,
        y: 2.84,
        z: 7.72,
      },
      rotation: { x: Math.PI * 1.5, y: 0, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 10, y: 5 },
      textureOffset: { x: 0, y: 0.3 },
      length: 6.05,
      width: 0.37,
      scale: 1,
      arrowSpeed: 0.004,
      // linkedPolygon: polygonArr[0],
    },
    showcaseTemp,
  );

  const outsidePipeHorizontal = await initTubeObject(
    {
      position: {
        x: 1.59,
        y: 9.75,
        z: 7.27,
      },
      rotation: { x: Math.PI, y: 0, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 1, y: 5 },
      textureOffset: { x: 0, y: 0.3 },
      length: 0.5,
      width: 0.38,
      scale: 1,
      arrowSpeed: 0.004,
      // linkedPolygon: polygonArr[0],
    },
    showcaseTemp,
  );

  const innerPipeX1 = await initTubeObject(
    {
      position: {
        x: -53.32,
        y: 0.69,
        z: 29.59,
      },
      rotation: { x: 0, y: Math.PI / 2, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 6, y: 5 },
      textureOffset: { x: 0, y: 0.3 },
      length: 4.75,
      width: 0.43,
      scale: 1,
      arrowSpeed: 0.004,
      // linkedPolygon: polygonArr[0],
    },
    showcaseTemp,
  );

  const innerPipeZ1 = await initTubeObject(
    {
      position: {
        x: -51.39,
        y: 0.74,
        z: 32.25,
      },
      rotation: { x: 0, y: Math.PI, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 2, y: 5 },
      textureOffset: { x: 0, y: 0.3 },
      length: 1.9,
      width: 0.43,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[1],
    },
    showcaseTemp,
  );

  const innerPipeZ2 = await initTubeObject(
    {
      position: {
        x: -53.76,
        y: 0.69,
        z: 32.18,
      },
      rotation: { x: 0, y: Math.PI, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 2, y: 5 },
      textureOffset: { x: 0, y: 0.3 },
      length: 1.9,
      width: 0.46,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[2],
    },
    showcaseTemp,
  );

  const innerPipeZ3Long = await initTubeObject(
    {
      position: {
        x: -56.29,
        y: 2.76,
        z: 40,
      },
      rotation: { x: 0, y: Math.PI, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 34, y: 5 },
      textureOffset: { x: 0, y: 0.3 },
      length: 18,
      width: 0.2,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[0],
    },
    showcaseTemp,
  );

  const innerPipeY1 = await initTubeObject(
    {
      position: {
        x: -55.55,
        y: 0.85,
        z: 24.97,
      },
      rotation: { x: 1.52, y: 0, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 2, y: 5 },
      textureOffset: { x: 0, y: 0.3 },
      length: 0.4,
      width: 0.13,
      scale: 1,
      arrowSpeed: 0.004,
      // linkedPolygon: polygonArr[0],
    },
    showcaseTemp,
  );

  const innerPipeY2 = await initTubeObject(
    {
      position: {
        x: -55.9,
        y: 0.43,
        z: 24.96,
      },
      rotation: { x: Math.PI * 1.5, y: 0, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 2, y: 5 },
      textureOffset: { x: 0, y: 0.3 },
      length: 0.4,
      width: 0.13,
      scale: 1,
      arrowSpeed: 0.004,
      linkedPolygon: polygonArr[0],
    },
    showcaseTemp,
  );

  const innerPipeY3 = await initTubeObject(
    {
      position: {
        x: -53.86,
        y: 0.43,
        z: 25.07,
      },
      rotation: { x: Math.PI * 1.5, y: 0, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 2, y: 5 },
      textureOffset: { x: 0, y: 0.3 },
      length: 0.4,
      width: 0.13,
      scale: 1,
      arrowSpeed: 0.004,
      // linkedPolygon: polygonArr[0],
    },
    showcaseTemp,
  );

  const innerPipeX2 = await initTubeObject(
    {
      position: {
        x: -55.14,
        y: 0.28,
        z: 24.95,
      },
      rotation: { x: 0, y: Math.PI * 0.5, z: 0 },
      visible: true,
      color: 'blue',
      textureRepeat: { x: 4, y: 5 },
      textureOffset: { x: 0, y: 0.3 },
      length: 1.1,
      width: 0.12,
      scale: 1,
      arrowSpeed: 0.004,
      // linkedPolygon: polygonArr[0],
    },
    showcaseTemp,
  );

  const dustPipe = await initTubeObject(
    {
      position: {
        x: -19,
        y: -0.85,
        z: 20.54,
      },
      rotation: { x: Math.PI * 1.5, y: 0, z: 0 },
      visible: true,
      color: '#b4d4bc',
      hasTexture: false,
      length: 6.05,
      width: 2.3,
      scale: 1,
      // linkedPolygon: polygonArr[0],
    },
    showcaseTemp,
  );

  animateDustPipe(dustPipe);

  const components = [
    outsidePipeVertical,
    outsidePipeHorizontal,
    innerPipeZ1,
    innerPipeZ2,
    innerPipeZ3Long,
    innerPipeX1,
    innerPipeY1,
    innerPipeY2,
    innerPipeY3,
    innerPipeX2,
  ];

  components.forEach((component) => {
    component.renderer = renderer;
  });

  return { components, polygonArr };
};
